/**
 * SecureEmbed Ingestion Activity
 *
 * Real-time war room view for active ingestion pipelines.
 * - Active pipeline cards with stage progress bars
 * - Resource impact panel (Lambda, OpenSearch, S3 metrics)
 * - Historical timeline (Gantt-style, last 24h)
 * - Auto-refresh at configurable interval
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, RefreshCw, Clock, AlertCircle, CheckCircle,
  Cpu, Database, HardDrive, Zap, ChevronDown, ChevronUp,
  Pause, Play, XCircle, Plus, Users, Building2, Server,
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { ingestionApi, jobsApi } from '../services/api';
import { DATASTORE_CARDS, DATASTORE_COLORS } from '../constants/datastores';

const ENVIRONMENTS = ['dev', 'test', 'uat', 'prod'];
const ENV_COLORS: Record<string, string> = {
  dev: 'bg-blue-600', test: 'bg-orange-600', uat: 'bg-purple-600', prod: 'bg-green-600',
};

const REFRESH_OPTIONS = [
  { label: '15s', value: 15000 },
  { label: '30s', value: 30000 },
  { label: '60s', value: 60000 },
  { label: 'Off', value: 0 },
];

const STAGES = ['Fetch', 'Transform', 'Embed', 'Index'];
const COLLABORATOR_STAGES = ['Reprocess', 'Harvest Institutions', 'Harvest Authors', 'ROR Enrich', 'Deduplicate', 'Embed'];
const COLLABORATOR_DATASTORES = new Set(['collaborator-directory']);

function getStagesForDatastore(datastore: string, pipelineStages?: string[]): string[] {
  if (pipelineStages && pipelineStages.length > 0) return pipelineStages;
  return COLLABORATOR_DATASTORES.has(datastore) ? COLLABORATOR_STAGES : STAGES;
}

interface CollaboratorStats {
  researchers: number;
  institutions: number;
  indexed: number;
  last_harvest: string | null;
  last_harvest_status: string | null;
  errors: string[] | null;
}

interface Pipeline {
  datastore: string;
  execution_arn?: string;
  stage: string;
  progress: number;
  start_time: string;
  input_params?: Record<string, any>;
  elapsed_seconds: number;
  stages?: string[];
  // Bulk/EC2 job fields
  execution_type?: 'ec2_bulk';
  job_id?: string;
  job_name?: string;
  mode?: string;
  total_target_count?: number;
  total_fetched?: number;
  ssm_command_id?: string;
  sources?: string[];
  source_keys?: string[];
  source_progress?: Record<string, { fetched: number; indexed: number }>;
  per_source_limits?: Record<string, number>;
  current_phase?: string;
  phase_detail?: string;
  current_source?: string;
  phase_pct?: number;
}

interface ResourceMetric {
  current: number;
  series: { timestamp: string; value: number }[];
  unit: string;
}

interface HistoryEntry {
  datastore: string;
  execution_arn: string;
  status: string;
  start_time: string;
  stop_time: string | null;
  duration_seconds: number | null;
}

interface LambdaBreakdownItem {
  function: string;
  short_name: string;
  invocations: number;
  category: string;
}

interface IndexBreakdownItem {
  index: string;
  doc_count: number;
  store_size: string;
  health: string;
  status: string;
}

interface ResourceBreakdown {
  lambda_breakdown: LambdaBreakdownItem[];
  index_breakdown: IndexBreakdownItem[];
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function getDatastoreLabel(key: string): string {
  const card = DATASTORE_CARDS.find(c => c.key === key);
  return card?.label || key;
}

function getDatastoreColor(key: string): string {
  return DATASTORE_COLORS[key]?.hex || '#6B7280';
}

function getSeverityColor(value: number, warn: number, crit: number): string {
  if (crit > 0 && value >= crit) return '#EF4444';
  if (warn > 0 && value >= warn) return '#F59E0B';
  return '#10B981';
}

// ─── Active Pipeline Card ───────────────────────────────────────────────────

const PipelineCard: React.FC<{ pipeline: Pipeline }> = ({ pipeline }) => {
  const color = getDatastoreColor(pipeline.datastore);
  const stages = getStagesForDatastore(pipeline.datastore, pipeline.stages);
  const stageIndex = stages.indexOf(pipeline.stage);
  const Icon = DATASTORE_CARDS.find(c => c.key === pipeline.datastore)?.icon || Activity;
  const [pausing, setPausing] = React.useState(false);
  const [stopping, setStopping] = React.useState(false);
  const [confirmStop, setConfirmStop] = React.useState(false);
  const [paused, setPaused] = React.useState(false);
  const jobId = pipeline.input_params?.job_id || pipeline.job_id || '';

  const handlePause = async () => {
    if (!jobId) return;
    setPausing(true);
    try {
      await jobsApi.pauseJob(jobId);
      setPaused(true);
    } catch (e) {
      console.error('Failed to pause pipeline', e);
    } finally {
      setPausing(false);
    }
  };

  const handleStop = async () => {
    if (!jobId) return;
    setStopping(true);
    try {
      await jobsApi.cancelJob(jobId);
    } catch (e) {
      console.error('Failed to stop pipeline', e);
    } finally {
      setStopping(false);
      setConfirmStop(false);
    }
  };

  return (
    <div className={`bg-white rounded-lg p-4 border ${paused ? 'border-orange-500/50' : 'border-gray-200'}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <Icon size={20} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-slate-800 font-medium truncate">{getDatastoreLabel(pipeline.datastore)}</h4>
          <p className="text-slate-500 text-xs">{formatElapsed(pipeline.elapsed_seconds)} elapsed</p>
        </div>
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
          paused ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
        }`}>
          {paused ? 'PAUSED' : pipeline.stage}
        </span>
      </div>

      {/* Stage progress bar */}
      <div className="flex gap-1 mb-2">
        {stages.map((stage, i) => {
          const isComplete = i < stageIndex;
          const isCurrent = i === stageIndex;
          const pctPerStage = 100 / stages.length;
          return (
            <div key={stage} className="flex-1 h-2 rounded-full overflow-hidden bg-gray-700">
              <div
                className={`h-full rounded-full transition-all duration-500 ${isCurrent && !paused ? 'animate-pulse' : ''}`}
                style={{
                  width: isComplete ? '100%' : isCurrent ? `${Math.max(20, (pipeline.progress - i * pctPerStage) / pctPerStage * 100)}%` : '0%',
                  backgroundColor: paused ? '#F59E0B' : color,
                  opacity: isComplete ? 1 : isCurrent ? 0.7 : 0.2,
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-gray-500">
        {stages.map(s => <span key={s} className="truncate">{s}</span>)}
      </div>

      {/* Pause & Stop buttons */}
      {jobId && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
          {!paused ? (
            <button
              onClick={handlePause}
              disabled={pausing}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors disabled:opacity-50"
            >
              <Pause size={12} />
              {pausing ? 'Pausing...' : 'Pause'}
            </button>
          ) : (
            <span className="text-xs text-orange-400 font-medium">Paused</span>
          )}
          {!confirmStop ? (
            <button
              onClick={() => setConfirmStop(true)}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
            >
              <XCircle size={12} />
              Stop
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-xs text-red-400">Are you sure?</span>
              <button
                onClick={handleStop}
                disabled={stopping}
                className="text-xs text-red-400 font-bold hover:text-red-300"
              >
                {stopping ? 'Stopping...' : 'Yes'}
              </button>
              <button
                onClick={() => setConfirmStop(false)}
                className="text-xs text-gray-500 hover:text-slate-700"
              >
                No
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Bulk Pipeline Card (EC2 Jobs) ──────────────────────────────────────────

const BulkPipelineCard: React.FC<{ pipeline: Pipeline }> = ({ pipeline }) => {
  const navigate = useNavigate();
  const color = getDatastoreColor(pipeline.datastore);
  const Icon = DATASTORE_CARDS.find(c => c.key === pipeline.datastore)?.icon || Activity;
  const progressPct = pipeline.progress || 0;
  const fetched = pipeline.total_fetched || 0;
  const target = pipeline.total_target_count || 0;
  const srcProgress = pipeline.source_progress || {};
  const srcKeys = pipeline.source_keys || [];
  const srcLimits = pipeline.per_source_limits || {};
  const allSources = [...new Set([...Object.keys(srcProgress), ...srcKeys])];
  const currentSource = pipeline.current_source || '';

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 col-span-full">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <Icon size={20} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-slate-800 font-medium truncate">
            {pipeline.job_name || getDatastoreLabel(pipeline.datastore)}
          </h4>
          <p className="text-slate-500 text-xs">
            {formatElapsed(pipeline.elapsed_seconds)} elapsed — Bulk / EC2
            {pipeline.current_phase && <span className="ml-2 text-blue-400">Phase: {pipeline.current_phase}</span>}
          </p>
        </div>
        <div className="text-right">
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-500/20 text-purple-400">BULK</span>
          <div className="text-xs text-gray-500 mt-1">{progressPct.toFixed(1)}%</div>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="mb-3">
        <div className="h-2 rounded-full overflow-hidden bg-gray-700">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.max(2, progressPct)}%`, backgroundColor: color }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{fetched.toLocaleString()} / {target.toLocaleString()} records</span>
          <span>{allSources.length} source(s) in parallel</span>
        </div>
      </div>

      {/* Phase detail */}
      {pipeline.phase_detail && (
        <div className="text-[11px] text-slate-500 mb-3 truncate bg-gray-50 rounded px-2 py-1 font-mono">
          {pipeline.phase_detail}
        </div>
      )}

      {/* Per-source parallel activity */}
      {allSources.length > 0 && (
        <div className="space-y-1.5 mb-3">
          <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">Parallel Source Activity</div>
          {allSources.map(src => {
            const sp = srcProgress[src];
            const srcFetched = sp?.fetched || 0;
            const srcLimit = srcLimits[src] || 0;
            const isActive = src === currentSource;
            const isDone = sp && srcFetched > 0 && (!isActive || (srcLimit > 0 && srcFetched >= srcLimit));
            const isPending = !sp || srcFetched === 0;
            const pct = srcLimit > 0 ? Math.min((srcFetched / srcLimit) * 100, 100) : (target > 0 ? Math.min((srcFetched / target) * 100 * allSources.length, 100) : 0);

            return (
              <div key={src} className={`flex items-center gap-2 rounded px-2 py-1.5 ${
                isActive ? 'bg-blue-900/30 border border-blue-700/50' :
                isDone ? 'bg-green-900/20 border border-green-800/30' :
                'bg-gray-50 border border-gray-200'
              }`}>
                {isActive && <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                </span>}
                {isDone && <CheckCircle size={10} className="text-green-500 shrink-0" />}
                {isPending && !isActive && <Clock size={10} className="text-gray-600 shrink-0" />}
                <span className={`text-xs font-medium w-28 shrink-0 uppercase ${
                  isActive ? 'text-blue-300' : isDone ? 'text-green-400' : 'text-gray-500'
                }`}>{src}</span>
                <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${
                    isActive ? 'bg-blue-500' : isDone ? 'bg-green-500' : 'bg-gray-600'
                  }`} style={{ width: `${Math.max(pct > 0 ? 1 : 0, pct)}%` }} />
                </div>
                <span className="text-[11px] font-mono text-slate-700 w-28 text-right shrink-0">
                  {srcFetched > 0 ? `${srcFetched.toLocaleString()}${srcLimit > 0 ? `/${(srcLimit/1000).toFixed(0)}k` : ''}` : '--'}
                </span>
                {isActive && <span className="text-[9px] bg-blue-800 text-blue-300 px-1 rounded">FETCH</span>}
                {isDone && <span className="text-[9px] bg-green-800/50 text-green-400 px-1 rounded">DONE</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Link to job detail */}
      {pipeline.job_id && (
        <div className="pt-3 border-t border-gray-200">
          <button
            onClick={() => navigate(`/ingestion/jobs/${pipeline.job_id}`)}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            View Job Details &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Resource Metric Card ───────────────────────────────────────────────────

const ResourceCard: React.FC<{
  label: string;
  icon: React.ElementType;
  metric: ResourceMetric | null;
  warn: number;
  crit: number;
  format?: (v: number) => string;
  drilldown?: React.ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
}> = ({ label, icon: Icon, metric, warn, crit, format, drilldown, expanded, onToggle }) => {
  const current = metric?.current ?? 0;
  const color = getSeverityColor(current, warn, crit);
  const displayValue = format ? format(current) : (typeof current === 'number' ? current.toFixed(1) : '0');
  const hasData = current > 0 && drilldown;

  return (
    <div className={`bg-white rounded-lg p-4 border ${expanded ? 'border-blue-500/50' : 'border-gray-200'} ${hasData ? 'cursor-pointer hover:border-iflow-400' : ''} transition-colors`}
      onClick={hasData ? onToggle : undefined}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="text-slate-500" />
        <span className="text-sm text-slate-500">{label}</span>
        {hasData && (
          <span className="ml-auto">
            {expanded ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
          </span>
        )}
      </div>
      <div className="flex items-end gap-3">
        <span className="text-2xl font-bold" style={{ color }}>{displayValue}</span>
        <span className="text-xs text-gray-500 mb-1">{metric?.unit || ''}</span>
      </div>
      {metric?.series && metric.series.length > 1 && (
        <div className="mt-2 h-[60px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={metric.series}>
              <defs>
                <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                fill={`url(#grad-${label})`}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {expanded && drilldown && (
        <div className="mt-3 pt-3 border-t border-gray-200" onClick={e => e.stopPropagation()}>
          {drilldown}
        </div>
      )}
    </div>
  );
};

// ─── Previous Runs Table ──────────────────────────────────────────────────

const PreviousRunsTable: React.FC = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const result = await jobsApi.listJobs({});
        const allJobs = result.jobs || [];
        // Aggregate run_history from all jobs
        const allRuns: any[] = [];
        for (const job of allJobs) {
          const history = (job as any).run_history || [];
          for (const run of history) {
            allRuns.push({
              ...run,
              job_id: job.job_id,
              job_name: (job as any).job_name || `Job ${job.job_id.slice(0, 8)}`,
              datastore: job.target_database,
            });
          }
        }
        // Sort by end_time descending
        allRuns.sort((a, b) => (b.end_time || '').localeCompare(a.end_time || ''));
        setRuns(allRuns.slice(0, 20));
      } catch (e) {
        console.error('Failed to load previous runs', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-500">
        <RefreshCw size={20} className="mx-auto animate-spin mb-2 opacity-50" />
        <p className="text-sm">Loading previous runs...</p>
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 bg-white rounded-lg">
        <Clock size={24} className="mx-auto mb-2 opacity-30" />
        <p className="text-sm">No completed runs yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Job</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Datastore</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Status</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Start</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Duration</th>
            <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Records</th>
            <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Errors</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run, i) => {
            const dsColor = DATASTORE_COLORS[run.datastore]?.hex || '#6B7280';
            const dsLabel = DATASTORE_CARDS.find(d => d.key === run.datastore)?.label || run.datastore;
            const duration = run.start_time && run.end_time
              ? formatRunDuration(new Date(run.end_time).getTime() - new Date(run.start_time).getTime())
              : '--';
            return (
              <tr
                key={`${run.run_id}-${i}`}
                className="border-b border-gray-200 hover:bg-gray-700/30 cursor-pointer"
                onClick={() => navigate(`/ingestion/jobs/${run.job_id}`)}
              >
                <td className="px-4 py-2.5 text-sm text-slate-700">{run.job_name}</td>
                <td className="px-4 py-2.5 text-sm" style={{ color: dsColor }}>{dsLabel}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-medium ${run.status === 'COMPLETED' ? 'text-green-400' : 'text-red-400'}`}>
                    {run.status}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-sm text-slate-500">
                  {run.start_time ? new Date(run.start_time).toLocaleString() : '--'}
                </td>
                <td className="px-4 py-2.5 text-sm text-slate-500">{duration}</td>
                <td className="px-4 py-2.5 text-sm text-slate-700 text-right">{(run.records_processed || 0).toLocaleString()}</td>
                <td className="px-4 py-2.5 text-sm text-right">
                  <span className={run.records_failed > 0 ? 'text-red-400' : 'text-gray-500'}>
                    {run.records_failed || 0}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function formatRunDuration(ms: number): string {
  if (ms < 0 || isNaN(ms)) return '--';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

// ─── Historical Timeline ────────────────────────────────────────────────────

const HistoryTimeline: React.FC<{ history: HistoryEntry[] }> = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Clock size={32} className="mx-auto mb-2 opacity-50" />
        <p>No ingestion activity in the last 24 hours</p>
      </div>
    );
  }

  // Group by datastore and build bars
  const now = Date.now();
  const windowStart = now - 24 * 60 * 60 * 1000;
  const datastoreMap = new Map<string, HistoryEntry[]>();
  for (const entry of history) {
    const list = datastoreMap.get(entry.datastore) || [];
    list.push(entry);
    datastoreMap.set(entry.datastore, list);
  }

  const datastores = Array.from(datastoreMap.keys()).sort();

  // Build chart data: one row per execution with x position = start hour offset, width = duration
  const chartData = datastores.map(ds => {
    const entries = datastoreMap.get(ds) || [];
    return {
      datastore: getDatastoreLabel(ds),
      datastoreKey: ds,
      entries: entries.map(e => {
        const start = new Date(e.start_time).getTime();
        const end = e.stop_time ? new Date(e.stop_time).getTime() : now;
        return {
          start: Math.max(0, (start - windowStart) / (1000 * 60 * 60)),
          duration: Math.max(0.1, (end - start) / (1000 * 60 * 60)),
          status: e.status,
          startTime: e.start_time,
          stopTime: e.stop_time,
          durationSeconds: e.duration_seconds,
        };
      }),
    };
  });

  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
      {/* Time axis */}
      <div className="flex items-center ml-36 text-[10px] text-gray-500 mb-1">
        {Array.from({ length: 7 }, (_, i) => (
          <span key={i} className="flex-1 text-center">
            {new Date(windowStart + i * 4 * 60 * 60 * 1000).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
          </span>
        ))}
      </div>
      {chartData.map(row => (
        <div key={row.datastoreKey} className="flex items-center gap-2">
          <div className="w-32 text-xs text-slate-500 truncate text-right flex-shrink-0">
            {row.datastore}
          </div>
          <div className="flex-1 h-6 bg-white rounded relative overflow-hidden">
            {row.entries.map((entry, i) => {
              const leftPct = (entry.start / 24) * 100;
              const widthPct = Math.max(0.5, (entry.duration / 24) * 100);
              const bgColor = entry.status === 'FAILED' ? '#EF4444'
                : entry.status === 'TIMED_OUT' ? '#F59E0B'
                : entry.status === 'RUNNING' ? '#3B82F6'
                : getDatastoreColor(row.datastoreKey);
              return (
                <div
                  key={i}
                  className={`absolute top-0.5 bottom-0.5 rounded-sm ${entry.status === 'RUNNING' ? 'animate-pulse' : ''}`}
                  style={{
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    backgroundColor: bgColor,
                    opacity: entry.status === 'SUCCEEDED' ? 0.8 : 1,
                  }}
                  title={`${row.datastore} — ${entry.status}\n${formatTime(entry.startTime)}${entry.stopTime ? ` → ${formatTime(entry.stopTime)}` : ' (running)'}\n${entry.durationSeconds ? formatElapsed(entry.durationSeconds) : ''}`}
                />
              );
            })}
          </div>
        </div>
      ))}
      {/* Legend */}
      <div className="flex gap-4 ml-36 mt-2 text-[10px] text-gray-500">
        {[
          { label: 'Succeeded', color: '#10B981' },
          { label: 'Running', color: '#3B82F6' },
          { label: 'Failed', color: '#EF4444' },
          { label: 'Timed Out', color: '#F59E0B' },
        ].map(item => (
          <div key={item.label} className="flex items-center gap-1">
            <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: item.color }} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────

const IngestionActivity: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [env, setEnv] = useState('dev');
  const [refreshInterval, setRefreshInterval] = useState(30000);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [totalActive, setTotalActive] = useState(0);
  const [resources, setResources] = useState<Record<string, ResourceMetric>>({});
  const [breakdown, setBreakdown] = useState<ResourceBreakdown | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [collabStats, setCollabStats] = useState<CollaboratorStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [activityRes, resourcesRes, historyRes, collabRes, breakdownRes] = await Promise.all([
        ingestionApi.getActivity({ env }).catch(() => ({ active_pipelines: [], total_active: 0 })),
        ingestionApi.getActivityResources({ env }).catch(() => ({ resources: {} })),
        ingestionApi.getActivityHistory({ env }).catch(() => ({ history: [] })),
        ingestionApi.getCollaboratorStats({ env }).catch(() => null),
        ingestionApi.getResourceBreakdown({ env }).catch(() => null),
      ]);
      setPipelines(activityRes.active_pipelines || []);
      setTotalActive(activityRes.total_active || 0);
      setResources(resourcesRes.resources || {});
      setHistory(historyRes.history || []);
      if (collabRes) setCollabStats(collabRes);
      if (breakdownRes) setBreakdown(breakdownRes);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch activity data');
    } finally {
      setLoading(false);
    }
  }, [env]);

  // Initial fetch + on env change
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(fetchAll, refreshInterval);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refreshInterval, fetchAll]);

  return (
    <div className="min-h-screen bg-iflow-50 text-slate-800 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        {!embedded && (
          <div className="flex items-center gap-3">
            <Activity size={24} className="text-blue-400" />
            <h1 className="text-xl font-bold">Ingestion Activity</h1>
            {totalActive > 0 && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                {totalActive} active
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-3">
          {/* Environment selector */}
          <div className="relative">
            <select
              value={env}
              onChange={e => setEnv(e.target.value)}
              className={`appearance-none pl-3 pr-8 py-1.5 rounded text-sm text-slate-800 border-0 cursor-pointer ${ENV_COLORS[env] || 'bg-gray-700'}`}
            >
              {ENVIRONMENTS.map(e => (
                <option key={e} value={e}>{e.toUpperCase()}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-800/60" />
          </div>

          {/* Auto-refresh selector */}
          <div className="relative">
            <select
              value={refreshInterval}
              onChange={e => setRefreshInterval(Number(e.target.value))}
              className="appearance-none bg-gray-700 pl-3 pr-8 py-1.5 rounded text-sm text-slate-800 border-0 cursor-pointer"
            >
              {REFRESH_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-800/60" />
          </div>

          {/* Manual refresh */}
          <button
            onClick={fetchAll}
            disabled={loading}
            className="p-1.5 rounded bg-gray-700 hover:bg-gray-600 transition-colors disabled:opacity-50"
            title="Refresh now"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>

          {/* Last refresh timestamp */}
          {lastRefresh && (
            <span className="text-xs text-gray-500">
              {lastRefresh.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Section 1: Active Pipelines */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3 text-slate-700">Active Pipelines</h2>
        {pipelines.length === 0 ? (
          (() => {
            const lambdaConcurrency = resources.lambda_concurrency?.current ?? 0;
            const indexingRate = resources.opensearch_indexing_rate?.current ?? 0;
            const hasActivity = lambdaConcurrency > 0 || indexingRate > 0;
            const activeLambdas = breakdown?.lambda_breakdown || [];
            const categories = activeLambdas.reduce<Record<string, { count: number; invocations: number; functions: string[] }>>((acc, fn) => {
              if (!acc[fn.category]) acc[fn.category] = { count: 0, invocations: 0, functions: [] };
              acc[fn.category].count++;
              acc[fn.category].invocations += fn.invocations;
              acc[fn.category].functions.push(fn.short_name);
              return acc;
            }, {});

            if (!hasActivity) {
              return (
                <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
                  <Activity size={32} className="mx-auto mb-2 opacity-30" />
                  <p>No active ingestion pipelines</p>
                </div>
              );
            }

            return (
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Server size={20} className="text-blue-400" />
                  <span className="text-sm font-medium text-blue-400">Background Activity Detected</span>
                  <span className="text-xs text-slate-500 ml-auto">No formal pipelines running — showing what is driving resource usage</span>
                </div>
                {activeLambdas.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Active Lambda Functions (last 5 min)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {Object.entries(categories).sort((a, b) => b[1].invocations - a[1].invocations).map(([cat, info]) => (
                        <div key={cat} className="bg-gray-50 rounded px-3 py-2 border border-gray-200">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-slate-700">{cat}</span>
                            <span className="text-xs text-slate-500">{info.count} fn{info.count > 1 ? 's' : ''}</span>
                          </div>
                          <p className="text-lg font-bold text-blue-400">{info.invocations.toLocaleString()}</p>
                          <p className="text-[10px] text-slate-500 mt-1 truncate" title={info.functions.join(', ')}>
                            {info.functions.slice(0, 3).join(', ')}{info.functions.length > 3 ? ` +${info.functions.length - 3}` : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {activeLambdas.length === 0 && hasActivity && (
                  <p className="text-xs text-slate-500">Loading breakdown...</p>
                )}
              </div>
            );
          })()
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pipelines.map(p => (
              p.execution_type === 'ec2_bulk'
                ? <BulkPipelineCard key={p.job_id || p.start_time} pipeline={p} />
                : <PipelineCard key={p.execution_arn || p.start_time} pipeline={p} />
            ))}
          </div>
        )}
      </section>

      {/* Section 2: Resource Impact */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3 text-slate-700">Resource Impact</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <ResourceCard
            label="Lambda Concurrency"
            icon={Zap}
            metric={resources.lambda_concurrency || null}
            warn={500}
            crit={900}
            format={v => Math.round(v).toString()}
            expanded={expandedCard === 'lambda'}
            onToggle={() => setExpandedCard(expandedCard === 'lambda' ? null : 'lambda')}
            drilldown={breakdown?.lambda_breakdown && breakdown.lambda_breakdown.length > 0 ? (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Top Functions (5 min)</p>
                {breakdown.lambda_breakdown.slice(0, 8).map(fn => (
                  <div key={fn.short_name} className="flex items-center justify-between py-1 text-xs">
                    <span className="text-slate-700 truncate mr-2" title={fn.short_name}>{fn.short_name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-slate-500">{fn.category}</span>
                      <span className="text-blue-400 font-medium">{fn.invocations}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : undefined}
          />
          <ResourceCard
            label="OpenSearch CPU"
            icon={Cpu}
            metric={resources.opensearch_cpu || null}
            warn={70}
            crit={85}
            expanded={expandedCard === 'cpu'}
            onToggle={() => setExpandedCard(expandedCard === 'cpu' ? null : 'cpu')}
            drilldown={breakdown?.index_breakdown && breakdown.index_breakdown.length > 0 ? (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Indexes by Size</p>
                {breakdown.index_breakdown.slice(0, 6).map(idx => (
                  <div key={idx.index} className="flex items-center justify-between py-1 text-xs">
                    <span className="text-slate-700 truncate mr-2">{idx.index}</span>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${idx.health === 'green' ? 'bg-green-500' : idx.health === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                  </div>
                ))}
              </div>
            ) : undefined}
          />
          <ResourceCard
            label="Indexing Rate"
            icon={Database}
            metric={resources.opensearch_indexing_rate || null}
            warn={-1}
            crit={-1}
            format={v => Math.round(v).toString()}
            expanded={expandedCard === 'indexing'}
            onToggle={() => setExpandedCard(expandedCard === 'indexing' ? null : 'indexing')}
            drilldown={breakdown?.index_breakdown && breakdown.index_breakdown.length > 0 ? (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Indexes by Doc Count</p>
                {breakdown.index_breakdown.slice(0, 8).map(idx => (
                  <div key={idx.index} className="flex items-center justify-between py-1 text-xs">
                    <span className="text-slate-700 truncate mr-2">{idx.index}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-slate-500">{idx.store_size}</span>
                      <span className="text-blue-400 font-medium">{idx.doc_count.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : undefined}
          />
          <ResourceCard
            label="S3 PUT Rate"
            icon={HardDrive}
            metric={resources.s3_put_rate || null}
            warn={-1}
            crit={-1}
            format={v => Math.round(v).toString()}
          />
        </div>
      </section>

      {/* Section 2b: Collaborator Directory */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3 text-slate-700">Collaborator Directory</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Users size={16} className="text-teal-400" />
              <span className="text-sm text-slate-500">Researchers</span>
            </div>
            <p className="text-2xl font-bold text-teal-400">
              {collabStats ? collabStats.researchers.toLocaleString() : '--'}
            </p>
            <p className="text-xs text-gray-500 mt-1">Count</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={16} className="text-teal-400" />
              <span className="text-sm text-slate-500">Institutions</span>
            </div>
            <p className="text-2xl font-bold text-teal-400">
              {collabStats ? collabStats.institutions.toLocaleString() : '--'}
            </p>
            <p className="text-xs text-gray-500 mt-1">Count</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Database size={16} className="text-teal-400" />
              <span className="text-sm text-slate-500">Indexed</span>
            </div>
            <p className="text-2xl font-bold text-teal-400">
              {collabStats ? collabStats.indexed.toLocaleString() : '--'}
            </p>
            <p className="text-xs text-gray-500 mt-1">OpenSearch Documents</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-teal-400" />
              <span className="text-sm text-slate-500">Last Harvest</span>
            </div>
            <p className="text-lg font-bold text-slate-800">
              {collabStats?.last_harvest ? formatTime(collabStats.last_harvest) : '--'}
            </p>
            {collabStats?.last_harvest_status && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${
                collabStats.last_harvest_status === 'SUCCEEDED' ? 'bg-green-500/20 text-green-400' :
                collabStats.last_harvest_status === 'RUNNING' ? 'bg-blue-500/20 text-blue-400' :
                collabStats.last_harvest_status === 'FAILED' ? 'bg-red-500/20 text-red-400' :
                'bg-gray-500/20 text-slate-500'
              }`}>
                {collabStats.last_harvest_status}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Section 3: Ingestion Jobs */}
      <section>
        <h2 className="text-lg font-semibold mb-3 text-slate-700">Ingestion Jobs</h2>
        <IngestionJobs env={env} />
      </section>

      {/* Section 4: Previous Runs */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3 text-slate-700">Previous Runs</h2>
        <PreviousRunsTable />
      </section>

      {/* Section 5: Historical Timeline */}
      <section>
        <h2 className="text-lg font-semibold mb-3 text-slate-700">Timeline (Last 24h)</h2>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <HistoryTimeline history={history} />
        </div>
      </section>
    </div>
  );
};

/* ── Ingestion Jobs Component ─────────────────────────────────────────────── */

interface IngestionJob {
  job_id: string;
  target_database: string;
  status: string;
  mode: string;
  total_target_count: number;
  progress_pct?: number;
  created_at: string;
  sources?: string[];
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-500/20 text-yellow-400',
  RUNNING: 'bg-blue-500/20 text-blue-400',
  COMPLETED: 'bg-green-500/20 text-green-400',
  FAILED: 'bg-red-500/20 text-red-400',
  PAUSED: 'bg-gray-500/20 text-slate-500',
  CANCELLED: 'bg-gray-500/20 text-gray-500',
};

const QUICK_START_PROFILES = [
  { id: 'scholarly-test', label: 'Test (100 docs)', desc: 'Quick test run' },
  { id: 'scholarly-1m', label: 'Scholarly 1M', desc: '1M papers, bulk mode' },
  { id: 'patents-500k', label: 'Patents 500K', desc: '500K patents, bulk mode' },
];

function IngestionJobs({ env }: { env: string }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<IngestionJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<string | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      const resp = await ingestionApi.getJobsList({ env, limit: 20 });
      setJobs(resp?.jobs || []);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const handleQuickStart = async (profileId: string) => {
    try {
      await ingestionApi.startJobProfile({ env, profile: profileId });
      fetchJobs();
    } catch (err) {
      console.error('Quick start failed:', err);
    }
  };

  const handleJobAction = async (jobId: string, action: 'pause' | 'resume' | 'cancel') => {
    setActionLoading(jobId);
    try {
      if (action === 'pause') await jobsApi.pauseJob(jobId);
      else if (action === 'resume') await jobsApi.resumeJob(jobId);
      else if (action === 'cancel') await jobsApi.cancelJob(jobId);
      setConfirmCancel(null);
      await fetchJobs();
    } catch (err) {
      console.error(`Job ${action} failed:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200 text-gray-500">
        Loading jobs...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick Start + Create Job */}
      <div className="flex gap-2 flex-wrap items-center">
        <button
          onClick={() => navigate('/ingestion/wizard')}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm text-white font-medium transition-colors"
        >
          <Plus className="inline w-3 h-3 mr-1" />Create Job
        </button>
        {QUICK_START_PROFILES.map(p => (
          <button
            key={p.id}
            onClick={() => handleQuickStart(p.id)}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm text-slate-700 border border-gray-600 transition-colors"
            title={p.desc}
          >
            <Zap className="inline w-3 h-3 mr-1" />{p.label}
          </button>
        ))}
      </div>

      {/* Cancel Confirmation Modal */}
      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-60" onClick={() => setConfirmCancel(null)} />
          <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4 border border-gray-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Cancel Job?</h3>
            <p className="text-sm text-slate-500 mb-4">
              This will permanently cancel the ingestion job. This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmCancel(null)}
                className="px-3 py-1.5 bg-gray-700 text-slate-700 rounded text-sm hover:bg-gray-600"
              >
                Keep Running
              </button>
              <button
                onClick={() => handleJobAction(confirmCancel, 'cancel')}
                disabled={!!actionLoading}
                className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === confirmCancel ? 'Cancelling...' : 'Cancel Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Jobs Table */}
      {jobs.length === 0 ? (
        <div className="bg-white rounded-lg p-6 border border-gray-200 text-gray-500 text-center">
          No ingestion jobs found. Use Create Job or quick-start buttons above.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-slate-500">
                <th className="px-4 py-2 text-left">Database</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Mode</th>
                <th className="px-4 py-2 text-right">Target</th>
                <th className="px-4 py-2 text-left">Progress</th>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job.job_id} className="border-b border-gray-200 hover:bg-gray-700/30">
                  <td className="px-4 py-2 text-gray-200">{job.target_database}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[job.status] || ''}`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-500">{job.mode}</td>
                  <td className="px-4 py-2 text-right text-slate-700">
                    {(job.total_target_count || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 w-40">
                    {(job.status === 'RUNNING' || job.status === 'PAUSED') && job.progress_pct !== undefined ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${job.status === 'PAUSED' ? 'bg-yellow-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(job.progress_pct, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">{job.progress_pct}%</span>
                      </div>
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">
                    {new Date(job.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      {job.status === 'RUNNING' && (
                        <>
                          <button
                            onClick={() => handleJobAction(job.job_id, 'pause')}
                            disabled={actionLoading === job.job_id}
                            className="p-1 rounded hover:bg-gray-600 text-yellow-400 transition-colors disabled:opacity-50"
                            title="Pause"
                          >
                            <Pause className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setConfirmCancel(job.job_id)}
                            disabled={actionLoading === job.job_id}
                            className="p-1 rounded hover:bg-gray-600 text-red-400 transition-colors disabled:opacity-50"
                            title="Cancel"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {job.status === 'PAUSED' && (
                        <>
                          <button
                            onClick={() => handleJobAction(job.job_id, 'resume')}
                            disabled={actionLoading === job.job_id}
                            className="p-1 rounded hover:bg-gray-600 text-green-400 transition-colors disabled:opacity-50"
                            title="Resume"
                          >
                            <Play className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setConfirmCancel(job.job_id)}
                            disabled={actionLoading === job.job_id}
                            className="p-1 rounded hover:bg-gray-600 text-red-400 transition-colors disabled:opacity-50"
                            title="Cancel"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default IngestionActivity;
